import { useCallback, useEffect, useRef } from 'react';
import useFluxActifs, { rythmeSelonFlux } from '@/shared/hooks/useFluxActifs';
import { echeanceRelecture, lotConcerne } from '@/shared/utils/evenementsServeur';

/**
 * Le rafraîchissement d'une liste d'actifs : sur événement quand le flux est ouvert,
 * toutes les 10 s quand il ne l'est pas. Partagé par Monitoring et Pilotage, pour que
 * les deux écrans ne divergent pas — c'est ce qui était arrivé à leurs catalogues de
 * commandes batterie.
 *
 * Trois règles :
 *   1. on ne relit que si le lot touche un actif AFFICHÉ (ou déborde) ;
 *   2. au plus une relecture tous les `INTERVALLE_MIN_MS` : une batterie publie chaque
 *      seconde, l'écran n'a pas à relire la page entière à ce rythme ;
 *   3. à la (re)connexion du flux, une relecture : ce qui a changé pendant la coupure
 *      n'a pas été annoncé.
 *
 * @param {object}   options
 * @param {function} options.relire          relit la liste affichée
 * @param {Set}      options.ids             identifiants des actifs affichés
 * @param {boolean}  options.actif           false = ni flux ni rafraîchissement
 * @param {number}   options.rythmeRapideMs  rythme imposé tant que le flux n'est pas ouvert
 *                                           (détail batterie ouvert : 4 s)
 * @param {number}   options.ecartTramesMs   écart minimal entre deux relectures causées
 *                                           par des TRAMES seules. Un ordre ou un relevé
 *                                           passe toujours en `INTERVALLE_MIN_MS`. Sert
 *                                           au Pilotage, dont la liste n'est pas paginée
 *                                           (0,7 s et 280 Ko mesurés sur le staging le
 *                                           30/09) : une trame par seconde l'aurait relue
 *                                           toutes les 2 s.
 * @returns {{ etat: string }} l'état du flux, pour l'afficher
 */
const INTERVALLE_MIN_MS = 2000;

export default function useRafraichissementTempsReel({
  relire, ids, actif = true, rythmeRapideMs = null, ecartTramesMs = INTERVALLE_MIN_MS,
}) {
  const relireRef = useRef(relire);
  relireRef.current = relire;
  const idsRef = useRef(ids);
  idsRef.current = ids;
  const derniere = useRef(0);
  const enAttente = useRef(null);
  const echeance = useRef(0);

  // Programme une relecture au plus tôt `ecartMs` après la précédente. Une demande plus
  // urgente (un ordre après une trame) avance l'échéance ; une moins urgente ne la
  // recule jamais.
  const relireBientot = useCallback((ecartMs = INTERVALLE_MIN_MS) => {
    const cible = echeanceRelecture({
      maintenant: Date.now(), derniere: derniere.current, ecartMs,
      prevue: enAttente.current ? echeance.current : null,
    });
    if (cible === null) return;
    clearTimeout(enAttente.current);
    echeance.current = cible;
    enAttente.current = setTimeout(() => {
      enAttente.current = null;
      derniere.current = Date.now();
      relireRef.current();
    }, cible - Date.now());
  }, []);

  useEffect(() => () => clearTimeout(enAttente.current), []);

  const { etat } = useFluxActifs({
    actif,
    onActifs: (lot) => {
      if (!lotConcerne(lot, idsRef.current)) return;
      const tramesSeules = (lot.causes || []).every(c => c === 'trame');
      relireBientot(tramesSeules ? ecartTramesMs : INTERVALLE_MIN_MS);
    },
  });

  useEffect(() => {
    if (actif && etat === 'temps_reel') relireBientot();
  }, [actif, etat, relireBientot]);

  const periode = rythmeRapideMs && etat !== 'temps_reel' ? rythmeRapideMs : rythmeSelonFlux(etat);
  useEffect(() => {
    if (!actif) return undefined;
    const tic = setInterval(() => relireRef.current(), periode);
    return () => clearInterval(tic);
  }, [actif, periode]);

  return { etat };
}
